"use client";

import { memo } from "react";
import Image from "next/image";
import Link from "next/link";
import { Play, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDuration, getVideoDisplayTitle } from "@/lib/utils";
import type { VideoResponse } from "@/types/api";
import { useLanguageStore, toContentLanguage } from "@/stores/language";

interface VideoCardProps {
  video: VideoResponse;
  "data-tour"?: string;
}

const STATUS_VARIANT = {
  COMPLETED: "success",
  FAILED: "error",
  PENDING_DOWNLOAD: "secondary",
  DOWNLOADING: "warning",
  PENDING_TRANSCRIPTION: "secondary",
  TRANSCRIBING: "warning",
  PENDING_TRANSLATION: "secondary",
  TRANSLATING: "warning",
  PENDING_EMBEDDING: "secondary",
  EMBEDDING_SUBTITLES: "warning",
} as const;

export const VideoCard = memo(function VideoCard({ video, "data-tour": dataTour }: VideoCardProps) {
  const lang = useLanguageStore((s) => s.language);
  const contentLang = toContentLanguage(lang);

  const isProcessing = video.status !== "COMPLETED" && video.status !== "FAILED";

  const dishName = getVideoDisplayTitle(video, contentLang);

  const subtitle =
    contentLang === "en"
      ? video.dishNameChinese ?? video.titleOriginal
      : video.dishNameEnglish ?? video.titleEnglish;

  return (
    <Link href={`/video/${video.id}`} className="group block" data-tour={dataTour}>
      <div className="relative aspect-[9/16] overflow-hidden rounded-xl bg-muted">
        {video.thumbnailUrl ? (
          <Image
            src={video.thumbnailUrl}
            alt={dishName}
            fill
            loading="lazy"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
            <Play className="h-12 w-12 text-muted-foreground" />
          </div>
        )}

        {/* Duration badge */}
        {video.durationSeconds && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white">
            <Clock className="h-3 w-3" />
            {formatDuration(video.durationSeconds)}
          </div>
        )}

        {/* Processing status overlay */}
        {isProcessing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
            <div className="mb-2 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span className="text-xs text-white">
              Processing... {video.processingProgress}%
            </span>
          </div>
        )}

        {/* Status badge for non-completed videos */}
        {video.status !== "COMPLETED" && (
          <div className="absolute left-2 top-2">
            <Badge variant={STATUS_VARIANT[video.status]}>
              {video.status.replace(/_/g, " ")}
            </Badge>
          </div>
        )}

        {/* Hover play button */}
        {video.status === "COMPLETED" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
            <div className="scale-0 rounded-full bg-primary p-4 transition-transform group-hover:scale-100">
              <Play className="h-8 w-8 text-white" fill="white" />
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1">
        <h3 className="line-clamp-2 text-sm font-medium leading-tight">
          {dishName}
        </h3>
        {subtitle && (
          <p className="line-clamp-1 text-xs text-muted-foreground">
            {subtitle}
          </p>
        )}
      </div>
    </Link>
  );
});
